import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { routeUrls } from "../../config/routeUrls";
import imageDocumentsDashboard from "../../assets/images/icons/documents-dashboard.svg";
import { ContractDashboard as ContractDashboardType } from "../../types/contracts";
import { contractsService } from "../../api/contractsService/contractsService";

const ContractsWidget: React.FC = () => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [contracts, setContracts] = useState<ContractDashboardType>({
    toBeAccepted: 0,
    others: 0,
    isMissingIBAN: 0
  });

  useEffect(() => {
    const fetchContracts = async () => {
      try {
        const contractsRes = await contractsService.dashboardContracts();
        if (contractsRes.success) {
          setContracts(contractsRes.responseBody);
        }
      } catch (error) {
        console.error('Error fetching contracts:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchContracts();
  }, []);

  const renderSkeletonLoader = () => {
    return (
      <div className="py-4 border-b w-full">
        <div className="font-black text-red text-[44px] leading-[53px] animate-pulse bg-gray-200 h-[53px] w-16 rounded"></div>
        <div className="text-lg leading-[22px] animate-pulse bg-gray-200 h-6 w-48 rounded mt-2"></div>
      </div>
    );
  };

  return (
    <div className="mb-[56px] md:mb-0 md:bg-lightGrey md:p-6 md:rounded card-shadow">
      <h2 className="font-light text-[24px] leading-[38px]">
        {t("screens.dashboard.widgets.contracts.heading")}
      </h2>

      {isLoading ? (
        <>
          {renderSkeletonLoader()}
          {renderSkeletonLoader()}
        </>
      ) : (
        <>
          {contracts.isMissingIBAN > 0 && (
            <div className="inline-flex mt-4 px-6 py-3 bg-[#FFEAED] border-[1.4px] border-red rounded-[4px] text-red font-medium items-center">
              <div className="flex items-center">
                <div>
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 18 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M18 9C18 13.879 13.879 18 9 18C4.121 18 0 13.879 0 9C0 4.121 4.121 0 9 0C13.879 0 18 4.121 18 9ZM16 9C16 5.206 12.794 2 9 2C5.206 2 2 5.206 2 9C2 12.794 5.206 16 9 16C12.794 16 16 12.794 16 9ZM10 10V5H8V10H10ZM10 13V11H8V13H10Z"
                      fill="#CF0A2C"
                    />
                  </svg>
                </div>
                <Link
                  to={routeUrls.contracts}
                  className="underline text-[16px] leading-[18px] ml-2"
                >
                  {t("screens.dashboard.widgets.contracts.bankDataMissing")}
                </Link>
              </div>
            </div>
          )}

          {(!!contracts.others || !!contracts.toBeAccepted) && (
            <>
              <div className="md:flex w-full lg:block xl:flex md:justify-between md:items-center">
                {contracts.toBeAccepted > 0 && (
                  <div className="py-4 border-b w-full">
                    <div className="font-black text-red text-[44px] leading-[53px]">
                      {contracts.toBeAccepted}
                    </div>
                    <div className="text-lg leading-[22px]">
                      {contracts.toBeAccepted === 1
                        ? t(
                            "screens.dashboard.widgets.contracts.toBeAcceptedContract"
                          )
                        : t(
                            "screens.dashboard.widgets.contracts.toBeAcceptedContracts"
                          )}
                    </div>
                  </div>
                )}

                {contracts.others > 0 && (
                  <div className="py-4 border-b w-full">
                    <div className="font-black text-red text-[44px] leading-[53px]">
                      {contracts.others}
                    </div>
                    <div className="text-lg leading-[22px]">
                      {contracts.others === 1
                        ? t(
                            "screens.dashboard.widgets.contracts.otherContract"
                          )
                        : t(
                            "screens.dashboard.widgets.contracts.otherContracts"
                          )}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {!!!contracts.others && !!!contracts.toBeAccepted && (
            <div className="mt-6">
              {t("screens.contracts.emptyContracts")}
            </div>
          )}
        </>
      )}

      <div className="mt-6">
        <Link
          className="inline-flex items-center"
          to={routeUrls.contracts}
        >
          <img src={imageDocumentsDashboard} alt="services_icon_bag" />
          <div className="uppercase text-sm leading-[17px] underline text-red font-semibold ml-[6px] mb-[2px]">
            {t("screens.dashboard.widgets.contracts.link")}
          </div>
        </Link>
      </div>
    </div>
  );
};

export default ContractsWidget; 